import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { ChevronLeft, Heart, DollarSign, Coffee, Gift, Sparkles, Check, Loader2, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DONATION_OPTIONS = [
  { id: 'coffee', amount: 1, label: '$1', period: 'Daily', description: 'A small blessing' },
  { id: 'support', amount: 5, label: '$5', period: 'Weekly', description: 'Support growth' },
  { id: 'growth', amount: 10, label: '$10', period: 'Monthly', description: 'Make an impact' },
  { id: 'impact', amount: 25, label: '$25', period: 'One-time', description: 'Big blessing' },
];

export default function Donate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedOption, setSelectedOption] = useState('coffee');
  const [donating, setDonating] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [donated, setDonated] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment status check timed out.');
      return;
    }

    setCheckingStatus(true);

    try {
      const response = await axios.get(`${API}/donate/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setDonated(true);
        toast.success('Thank you for your generous gift! 🙏');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired.');
        return;
      }
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      toast.error('Error checking payment.');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleDonate = async () => {
    setDonating(true);
    
    try {
      const response = await axios.post(`${API}/donate/checkout`, {
        package_id: selectedOption,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Could not process donation.');
      setDonating(false);
    }
  };

  const selectedAmount = DONATION_OPTIONS.find(o => o.id === selectedOption)?.amount || 1;

  if (checkingStatus) {
    return (
      <div className="min-h-screen sky-gradient flex flex-col items-center justify-center px-6">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-600 font-medium">Processing your gift...</p>
      </div>
    );
  }

  if (donated) {
    return (
      <div className="min-h-screen sky-gradient flex flex-col items-center justify-center px-6 pb-safe">
        <div className="text-center animate-fade-in">
          <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Check className="w-12 h-12 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-3">Thank You!</h1>
          <p className="text-gray-600 mb-8 max-w-sm">
            Your generosity helps spread kindness and blessings to people around the world.
          </p>
          <Button
            onClick={() => navigate('/home')}
            className="rounded-full px-8 bg-blue-500 hover:bg-blue-600"
            data-testid="back-to-home"
          >
            Back to Home
          </Button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen sky-gradient pb-safe" data-testid="donate-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-6 text-center">
        <div className="w-20 h-20 rounded-full shadow-float mx-auto mb-4 overflow-hidden">
          <img src="/images/logo.png" alt="BlessBe" className="w-20 h-20 object-cover" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Make Today Better</h1>
        <p className="text-gray-600 max-w-xs mx-auto">
          Your gift helps spread kindness and blessings around the world
        </p>
      </header>

      <main className="px-6 space-y-6">
        {/* Charity Notice */}
        <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
          <p className="text-sm text-blue-700 text-center">
            🌍 All blessings will be disbursed to our charity partners helping millions of people all over the world
          </p>
        </div>

        {/* Donation Options */}
        <div className="grid grid-cols-2 gap-3">
          {DONATION_OPTIONS.map((option) => {
            const isSelected = selectedOption === option.id;
            return (
              <button
                key={option.id}
                onClick={() => setSelectedOption(option.id)}
                className={`p-4 rounded-2xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-blue-300'
                }`}
                data-testid={`amount-${option.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold text-gray-800">{option.label}</span>
                  {isSelected && <Check className="w-5 h-5 text-blue-500" />}
                </div>
                <p className="text-sm text-blue-600 font-medium">{option.period}</p>
                <p className="text-xs text-gray-500">{option.description}</p>
              </button>
            );
          })}
        </div>

        {/* Give Button */}
        <Button
          onClick={handleDonate}
          disabled={donating}
          className="w-full h-14 rounded-2xl text-lg font-bold bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl transition-all"
          data-testid="donate-button"
        >
          {donating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <DollarSign className="w-5 h-5 mr-2" />
              Give ${selectedAmount}
            </>
          )}
        </Button>

        {/* Secure Payment Note */}
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <CreditCard className="w-4 h-4" />
          <span className="text-sm">Secure payment via Stripe</span>
        </div>

        {/* What your gift does */}
        <Card className="rounded-2xl border-0 shadow-card bg-white">
          <CardContent className="p-5">
            <h3 className="font-bold text-gray-800 mb-4">Your Gift Helps</h3>
            <div className="space-y-3">
              {[
                { icon: '📖', text: 'Daily devotionals for thousands' },
                { icon: '🙏', text: 'AI-powered prayer support' },
                { icon: '💝', text: 'Spreading kindness worldwide' },
                { icon: '🌍', text: 'Keeping BlessBe free for all' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-xl">{item.icon}</span>
                  <span className="text-gray-600">{item.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Scripture */}
        <div className="text-center py-4">
          <p className="font-medium italic text-gray-600 mb-1">
            &ldquo;God loves a cheerful giver.&rdquo;
          </p>
          <p className="text-sm text-blue-600">— 2 Corinthians 9:7</p>
        </div>
      </main>

      <BottomNav />
    </div>
  );
}
